'use strict';
// Pure render helpers for the Order Chat document card (Task 9). CommonJS so
// Jest can unit-test them directly (the v3 bundle has no ESM transform under
// Jest); the JSX imports them through the ESM re-export sibling
// (order-doc-helpers.js). No React / DOM here — just {label,tone} descriptors
// the component maps to inline-style tokens.

// The two order MODES the document card renders. quest_homebound is a mobile
// phlebotomy / home-draw order; everything else lab routes through an Elation
// electronic requisition ("e-Req"). Anything that isn't quest_homebound falls
// back to the e-Req badge — the component only ever shows this for lab/quest
// cards, so the fallback is the safe default.
function modeBadge(type) {
  if (type === 'quest_homebound') return { label: 'Homebound draw', tone: 'home' };
  return { label: 'e-Req', tone: 'ereq' };
}

// Per-test diagnosis-resolution confidence → a chip. high/medium read as a
// confidence level; low (or unknown) reads as a CALL TO ACTION ("Confirm")
// because that test still needs the provider to pick/verify the ICD-10.
// Tones map to the .orderchat-doc-chip--{ok,warn,danger} CSS classes.
function confidenceChip(confidence) {
  if (confidence === 'high') return { label: 'High', tone: 'ok' };
  if (confidence === 'medium') return { label: 'Medium', tone: 'warn' };
  return { label: 'Confirm', tone: 'danger' };
}

// The disclosures an order carries — why it routes where it does (a default or
// a substituted lab), an allergy, a coverage gap. The chat text leads with them
// ("Heads up — …"); the CARD rendered none, so a provider who reviews the card
// instead of the bubble saw no reason for the destination they are approving.
// Accepts either the card DTO (`warnings`) or a raw extracted order
// (`_warnings`) — the summary card renders straight off the order.
function warningLines(source) {
  const raw = (source && (source.warnings || source._warnings)) || [];
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((w) => String(w == null ? '' : w).trim()).filter(Boolean))];
}

// (F12) The advisory blocks a tier of candidates needs — "these are age/sex
// guesses, unverified against any coverage source". The resolver emits them as
// contextBlocks and the FAX rail renders them, but the card's DTO carries only
// {status,confidence,source,candidates}, so a home-draw row offering twelve
// unverified codes said nothing about what the list IS; only the per-option
// marks hinted at it. Derived here from the candidates the row already holds —
// the smaller half of the fix: no new wire field, and rows staged before the
// deploy get the line too. The one thing the DTO cannot carry is the provider's
// age·sex factor, so the copy omits the numbers.
//
// (r2/C3+F3+F4) The card emits the block SET the provider would for the same
// options, not one block chosen by source: a vendor candidate that is also an
// age/sex guess needs BOTH caveats (confirm the patient has it AND confirm
// against CMS), a covers-any row leads with "Broadly covered" the way the rail
// does, and "covered" stays reserved for the CMS-grounded tier. The ai body
// carries no demographic clause at all: on the card, `source:'ai'` has two
// producers — the age/sex tier and the interpret text fallback — and "for a
// patient like this" is false of a candidate derived from typed text.
const _aiBlock = (label) => ({
  kind: 'ai_advisory',
  severity: 'unverified',
  title: 'Candidate diagnoses (unverified)',
  body: `Best-guess ICD-10 candidates for ${label} — NOT verified against any payer coverage source. The clinician must confirm a diagnosis the chart supports.`,
});
const _vendorBlock = (label) => ({
  kind: 'vendor_advisory',
  severity: 'unverified',
  title: 'Lab decision-support',
  // (F11) the list, never the lab that published it — this card's order may go
  // to a different vendor entirely.
  body: `A lab's courtesy ICD-10 list for ${label} — a top-utilized subset; confirm against the CMS policy before relying on it.`,
});
const _demographicBlock = (label, grounded) => ({
  kind: 'demographic_likely',
  severity: 'covered',
  // "covered" is reserved for the CMS-grounded tier (those codes ARE in the
  // covered set); a courtesy-list or covers-any example list drops the word.
  title: grounded ? 'Common covered indications' : 'Common indications',
  body: `These diagnoses commonly justify ${label} for a patient like this — CONFIRM the patient actually has the condition; do not document a diagnosis they don't have.`,
});
const _coversAnyBlock = (label) => ({
  kind: 'covers_any',
  severity: 'ok',
  title: 'Broadly covered',
  body: `${label} is covered for essentially any clinically relevant diagnosis (a few specific diagnoses are excluded). Pick the charted diagnosis that is the reason for the test, or search one — confirm it supports the order.`,
});
const TIER_BLOCK = { ai: _aiBlock, vendor: _vendorBlock, demographic: _demographicBlock };

// The blocks the provider would emit for this row's options. The tier is read
// off the OPTIONS, not the row's `source`, because a staged row's source is its
// screening SEED while the candidates under the seed are the tier that needs the
// caveat. A purely chart/search-grounded row gets none (the coverage flag and
// the chart group already speak).
function tierAdvisories(options, testLabel) {
  const label = (typeof testLabel === 'string' && testLabel.trim()) || 'this test';
  const opts = (options || []).filter(Boolean);
  const blocks = [];
  // A covers-any NCD covers essentially anything: the rail leads with that fact,
  // above whatever tier filled the list out.
  if (opts.some((o) => o.broadlyCovered)) blocks.push(_coversAnyBlock(label));
  const tiered = opts.find((o) => TIER_BLOCK[o.source]);
  if (tiered) {
    // "covered" only when the code really is in the CMS covered set for this
    // test — a covers-any example is not an enumerated match.
    const grounded = tiered.status === 'covered' && !tiered.broadlyCovered;
    if (tiered.source === 'vendor') {
      // Both caveats, in the provider's order: the demographic guess about the
      // PATIENT, then the courtesy list's own caveat about the POLICY.
      if (tiered.demographicLikely) blocks.push(_demographicBlock(label, false));
      blocks.push(_vendorBlock(label));
    } else {
      blocks.push(TIER_BLOCK[tiered.source](label, grounded));
    }
  }
  return blocks;
}

// The generic field-context the per-test dx rail renders, built from ONE row of
// the card's perTestResolution — the same normalized shape fetchFieldOptions
// emits for the fax form-fill rail.
function railContextForRow(row) {
  const t = row || {};
  const options = (Array.isArray(t.candidates) ? t.candidates : []).map((c) => ({
    value: c.code,
    label: c.description ? `${c.code} — ${c.description}` : c.code,
    status: c.status,
    source: c.source,
    onChart: c.onChart,
    // Caveat flags must survive into the rail so a guess renders "likely —
    // confirm" (demographicLikely) and never plain green (broadly/prefix).
    ...(c.demographicLikely ? { demographicLikely: true } : {}),
    ...(c.broadlyCovered ? { broadlyCovered: true } : {}),
    ...(c.prefixParent ? { prefixParent: true } : {}),
    ...(c.coveredForTest ? { coveredForTest: true } : {}),
  }));
  return {
    options,
    search: { enabled: true, placeholder: 'Type a diagnosis in plain English → ICD-10…' },
    coverageChecked: t.status === 'covered',
    contextBlocks: tierAdvisories(options, t.name),
  };
}

// (F8) The draw schedule dictated for ONE test — fasting / frequency / start /
// finish. All four print into the requisition's REQUIRED columns and none of
// them were visible on the card, so "fasting, monthly for six months" could
// reach Quest without the provider ever seeing it on screen. Returns one line,
// or null when nothing was dictated (rather than an empty line).
//
// Fasting prints as "Y" or blank on the requisition, NEVER "N" (deliberate), so
// `fasting:false` says nothing here either: the card must not make a claim the
// faxed page does not.
function _mmddyyyy(iso) {
  const s = String(iso == null ? '' : iso).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;   // anything non-ISO passes through as dictated
}
function scheduleLine(schedule) {
  const s = schedule || {};
  const parts = [];
  if (s.fasting === true) parts.push('Fasting');
  const freq = typeof s.frequency === 'string' ? s.frequency.trim() : '';
  if (freq) parts.push(freq);
  const start = s.startDate ? _mmddyyyy(s.startDate) : '';
  const finish = s.finishDate ? _mmddyyyy(s.finishDate) : '';
  if (start && finish) parts.push(`${start}–${finish}`);
  else if (start) parts.push(`from ${start}`);
  else if (finish) parts.push(`through ${finish}`);
  return parts.length ? parts.join(' · ') : null;
}

// The ORDER-level indication as a code, from the card DTO's `orderDx`
// ({code, description} | null). One reader, so every count below agrees about
// what "the order has an indication" means: a dx object with no CODE is not one
// (nothing would print).
function _orderDxCode(orderDx) {
  return (orderDx && orderDx.code) ? String(orderDx.code) : '';
}

// How many rows would fax with NO diagnosis on the line. A "suggested" seed
// counts as filled — it is a real code on the requisition that the provider
// verifies; only an empty cell is a blank line to Quest.
//
// (F4) A row with no code of its own is NOT one of them when the order carries
// an indication: the requisition prints the order's codes on every such line
// (questHomebound/fromExtraction._icd, and content.icd10_codes on an e-Req), so
// under an order-level indication no line goes out blank. That includes the one
// row the DTO deliberately leaves `needs_dx` under an order-level dx — a line
// whose OWN dictated indication resolved to nothing (R7/R8): the row keeps the
// provider's attention, this count keeps the requisition's truth.
function blankDxCount(rows, orderDx) {
  if (_orderDxCode(orderDx)) return 0;
  return (Array.isArray(rows) ? rows : []).filter((r) => r && !r.code).length;
}

// The "Review & send" label. F3: the button is ENABLED whatever the rows say — it
// opens the PDF preview, which is also the only surface a prerequisite failure
// (missing Quest account #, unresolved physician) is ever visible on. It used to
// be painted grey with a "Some tests still need a diagnosis" tooltip, so the one
// control that could show that error read as switched off, and the owner's own
// rehearsal went to Save draft instead and lost the message. The count now rides
// in the LABEL instead of the colour.
function reviewSendLabel(rows, orderDx) {
  const n = blankDxCount(rows, orderDx);
  if (!n) return 'Review & send';
  return `Review & send · ${n} ${n === 1 ? 'needs' : 'need'} a diagnosis`;
}

// The blank-diagnosis warning (owner ruling O61, 2026-09-03): a home-draw
// requisition MAY leave with lines that carry no ICD-10 — real orders pool
// diagnoses and validate.js requires one across the order, not one per test — but
// Quest's own form says only ICD-10 codes are processed, so the provider is told
// how many lines are blank and decides. A warning, never a block: draft and fax
// both stay available. '' when every row has a code.
function blankDxWarning(rows, orderDx) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const n = blankDxCount(list, orderDx);
  if (!n) return '';
  const noun = list.length === 1 ? 'test' : 'tests';
  return `${n} of ${list.length} ${noun} ${n === 1 ? 'has' : 'have'} no diagnosis — Quest may not process those lines`;
}

// What a SET order-level indication actually does to the document, in one
// sentence under the pill. `lines` is how many lines the requisition prints it
// on — every line with no code of its own (the card computes it; `_icd` is what
// prints it).
//
// The number decides three words at once — has/have, test/tests, its/their —
// and the sandbox card of 2026-09-03 got the last one wrong: "Printed on 1 test
// that has no diagnosis of their own" (report 686 acceptance, ledger copy nit).
// The whole sentence lives here so a count and its grammar cannot drift apart
// again, the zero case included: nothing to print on is not "0 tests", it is
// the indication saying it reaches the requisition nowhere.
function orderDxPrintedLine(lines) {
  const n = Number(lines) || 0;
  if (!n) return 'Every test has its own diagnosis — this is not printed on the requisition';
  return n === 1
    ? 'Printed on 1 test that has no diagnosis of its own'
    : `Printed on ${n} tests that have no diagnosis of their own`;
}

// The identity of what the requisition would PRINT for these rows — the card
// dismisses a rendered PDF once this changes, so everything a card gesture can
// change about a printed line belongs in it:
//   • the test NAME and its resolution STATUS (an `order_dx` row given its own
//     code equal to the order-level primary prints all the order's codes before
//     and that one after, while name and primary code stay put);
//   • the CATALOG IDENTITY — testId / testCode. "Pick this alternative" can swap
//     a row onto a same-NAME row of another compendium; the generic-lab flow keys
//     its Elation draft on the test id (genericLabReq's payload hash), so the
//     order behind the preview is REPLACED while the visible name is unchanged;
//   • EVERY ICD-10 on the line with its description — both are printed on the
//     generic requisition, and `set-icd10` collapses a multi-code row to one;
//   • the SCHEDULE the requisition prints for that line (fasting / frequency /
//     window, F8's display-only DTO field). No card gesture changes it, but a
//     re-dictation into the same session can, and then the same names and codes
//     would fax on a different cadence.
//   • the ORDER-LEVEL indication (F4): `set-order-icd10` prints its code on
//     every line that has none of its own, so one gesture rewrites the ICD-10
//     column of a rendered PDF — including on a line the DTO still shows as
//     needs_dx (its own dictated indication resolved to nothing), where nothing
//     about the ROW changes at all. Carried unconditionally: whether a line is
//     blank is itself one edit away, and a preview that closes once too often
//     costs a click, while one that stays open too long is the wrong document.
// The generic destination fax is the one printed thing that is edited INSIDE the
// pane, so it is deliberately absent.
function rowsKeyFor(rows, orderDx) {
  const list = Array.isArray(rows) ? rows : [];
  const orderCode = _orderDxCode(orderDx);
  // Prepended only when there IS one, so a card with no order-level indication
  // keys byte-identically to before this field existed.
  const head = orderCode ? [[orderCode, (orderDx && orderDx.description) || null]] : [];
  return JSON.stringify([...head, ...list.map((r) => [
    r && r.name,
    r && r.status,
    r && r.testId,
    r && r.testCode,
    r && r.schedule && r.schedule.fasting,
    r && r.schedule && r.schedule.frequency,
    r && r.schedule && r.schedule.startDate,
    r && r.schedule && r.schedule.finishDate,
    ...(((r && r.codes) || []).map((c) => [(c && c.code) || c, c && c.description])),
    // A row whose only code lives in `code` (no `codes` array) still counts.
    ...(((r && r.codes) || []).length ? [] : [r && r.code]),
  ])]);
}

module.exports = { modeBadge, confidenceChip, warningLines, railContextForRow, tierAdvisories, scheduleLine, blankDxCount, reviewSendLabel, blankDxWarning, orderDxPrintedLine, rowsKeyFor };
